import { useMemo } from "react";
import { subscribeKey } from "valtio/utils";

import {
  __ONLY_WRITE_STATE_FROM_ACTIONS as writeState,
  readState,
} from "@/__main__/app-state.mjs";
import { IS_APP, isPersistent, MAX_TIME } from "@/__main__/constants.mjs";
import { readPath } from "@/__main__/get-data.mjs";
import { freshInitialState } from "@/__main__/initial-state.mjs";
import blitzMessage, { EVENTS, initEvents } from "@/__main__/ipc-core.mjs";
import {
  ACTIVE_GAMES,
  GAME_SHORT_NAMES,
  GAME_SYMBOL_BY_SHORT_NAME,
} from "@/app/constants.mjs";
import { GAME_SYMBOL_CS2 } from "@/game-csgo/definition-symbol.mjs";
import type {
  RANK_SYMBOLS,
  ROLE_SYMBOLS,
} from "@/game-lol/constants/constants.mjs";
import { GAME_SYMBOL_LOL } from "@/game-lol/definition-symbol.mjs";
import { GAME_SYMBOL_VAL } from "@/game-val/definition-symbol.mjs";
import type {
  GameOverlay,
  OverlaySettingKey,
} from "@/hub-overlays/constants.mjs";
import {
  GAME_OVERLAY_SETTINGS,
  GAME_OVERLAYS,
  OVERLAY_GAMES,
} from "@/hub-overlays/constants.mjs";
import { GAME_OVERLAY_DB_KEYS } from "@/library/constants.mjs";
import { useReRender } from "@/shared/EventedRender.jsx";
import clone from "@/util/clone.mjs";
import { devError, devWarn } from "@/util/dev.mjs";
import diff from "@/util/diff.mjs";
import digPath from "@/util/dig-path.mjs";
import gameShortNameReassign from "@/util/game-short-name-reassign.mjs";
import { nullishMerge } from "@/util/optional-merge.mjs";
import partition from "@/util/partition.mjs";
import subscriptionStateDiff from "@/util/subscription-state-diff.mjs";
import SymbolMap from "@/util/symbol-map.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

// prefer use this instead of game db key for overlay settings in app state
export const OVERLAYS = "overlays";

async function _readCoreDb<T>(key: string): Promise<T> {
  await initEvents;
  return JSON.parse((await blitzMessage(EVENTS.DB_READ, key)) as string);
}

async function _writeCoreDb<T>(key: string, value: T): Promise<boolean> {
  await initEvents;
  return blitzMessage(EVENTS.DB_WRITE, {
    key,
    value: JSON.stringify(value),
  }) as Promise<boolean>;
}

export function launchGame(symbol: symbol) {
  return blitzMessage(EVENTS.START_GAME, {
    gameKey: gameShortNameReassign(GAME_SHORT_NAMES[symbol]),
    args: {},
  });
}

// initializes overlay default settings and inits a listener to keep overlay settings in sync with core
export function initOverlaySettings(settings) {
  // must subscribe to settings and not its inner keys, since we only write to this entire object
  if (IS_APP)
    subscribeKey(
      readState,
      "settings",
      subscriptionStateDiff(handleOverlaySettingsChange, readState.settings),
    );

  for (const [game, defaultCfg] of GAME_OVERLAY_SETTINGS) {
    const gameKey = GAME_SHORT_NAMES[game];
    const currentCfg = settings[gameKey]?.[OVERLAYS] ?? {};

    if (!settings[gameKey]) settings[gameKey] = {};
    settings[gameKey][OVERLAYS] = nullishMerge(currentCfg, defaultCfg);
  }
}

export function handleOverlaySettingsChange(next, prev) {
  if (!IS_APP) return;
  const delta = diff(prev, next);
  if (!delta) return;
  OVERLAY_GAMES.filter(
    (game) => delta[GAME_SHORT_NAMES[game]]?.[OVERLAYS],
  ).forEach((game) =>
    // write settings to core
    _writeCoreDb(
      GAME_OVERLAY_DB_KEYS[game],
      next[GAME_SHORT_NAMES[game]][OVERLAYS],
    ),
  );
}

//Note(Liz3): not sure if these will be the same 1:1 but for now i took the mappings from web(blitz-web)
const OVERLAY_HOTKEY_GAME_KEY_MAP = {
  [GAME_SYMBOL_LOL]: ["srOverlayToggleKey"],
  [GAME_SYMBOL_VAL]: ["valPostmatchOverlayToggleKey"],
  [GAME_SYMBOL_CS2]: ["csgoOverlayToggleKey"],
};
export const getOverlayHotKeyValue = (game) => {
  const mapping = OVERLAY_HOTKEY_GAME_KEY_MAP[game];
  const value = readOverlaySettings(game, mapping);
  if (!value) return "Tab";
  return value.value;
};
export const writeOverlayHotkey = (data, game) => {
  const mapping = OVERLAY_HOTKEY_GAME_KEY_MAP[game];
  if (!mapping) return null;
  return writeOverlaySetting(game, mapping, data);
};

export function readOverlaySettings(
  game: symbol,
  path?: readonly string[],
): GameOverlayData {
  const gameKey: string = GAME_SHORT_NAMES[game];

  const overlaySettings = readState.settings[gameKey]?.[OVERLAYS];

  // If there is a path then read into it
  if (path) return readPath(path, overlaySettings);

  // return now unless they specify a path
  return overlaySettings;
}

export function writeOverlaySetting<T>(
  game: symbol,
  key: OverlaySettingKey,
  value: T,
) {
  const dbKey = GAME_OVERLAY_DB_KEYS[game];
  const gameKey = GAME_SHORT_NAMES[game];
  if (!dbKey) return;

  const settings = clone(readState.settings || freshInitialState.settings);
  const overlaySettingRoot = settings[gameKey][OVERLAYS];

  // Currently this can fail if the full path doesn't exist
  const [writePath, writeKey] = digPath(key, overlaySettingRoot);
  if (!writePath) {
    devError(`Failed to write overlay setting "${String(writeKey)}"`);
    return;
  }

  writePath[writeKey] = value;
  // execute the write to app state
  settings[isPersistent] = MAX_TIME;
  writeState.settings = settings;
}

// must add (game) to router fetch
export const useOverlaySettings = (game: symbol) => {
  const { settings } = useSnapshot(readState);
  const gameKey = GAME_SHORT_NAMES[game];
  return settings?.[gameKey]?.[OVERLAYS] ?? {};
};

export const useActiveOverlays = (game?: symbol, active?: boolean) => {
  const renderValue = useReRender();
  const { settings } = useSnapshot(readState);
  const games = game ? [game] : SymbolMap.keys(GAME_OVERLAY_DB_KEYS);
  const overlaySettings = SymbolMap.fromEntries(
    games.map((g) => [
      g,
      useMemo(() => {
        const gameConfig = settings[GAME_SHORT_NAMES[g]]?.[OVERLAYS];
        const [activeEntries, inactiveEntries] = partition(
          Object.entries(GAME_OVERLAYS[g] ?? {}),
          ([, overlay]) => readPath(overlay.key, gameConfig),
        );
        return active
          ? Object.fromEntries(activeEntries)
          : active === false
            ? Object.fromEntries(inactiveEntries)
            : [
                Object.fromEntries(activeEntries),
                Object.fromEntries(inactiveEntries),
              ];
      }, [settings[GAME_SHORT_NAMES[g]]?.[OVERLAYS], active, renderValue]),
    ]),
  );

  return game ? overlaySettings[game] : overlaySettings;
};

export const useOverlays = (game?: (typeof OVERLAY_GAMES)[number]) => {
  const renderValue = useReRender();
  const { settings } = useSnapshot(readState);
  if (game && !OVERLAY_GAMES.includes(game)) return;
  const games = game
    ? [game]
    : OVERLAY_GAMES.filter((g) => (ACTIVE_GAMES as symbol[]).includes(g));

  const overlaySettings = SymbolMap.fromEntries(
    games.map((g) => [
      g,
      useMemo(() => {
        const gameConfig = settings[GAME_SHORT_NAMES[g]]?.[OVERLAYS];
        const gameOverlays = Object.fromEntries(
          Object.entries(GAME_OVERLAYS[g]).map(([k, overlay]) => [
            k,
            {
              ...overlay,
              isActive: readPath(overlay.key, gameConfig) ?? false,
            },
          ]),
        );
        return gameOverlays;
      }, [settings[GAME_SHORT_NAMES[g]]?.[OVERLAYS], renderValue]),
    ]),
  );

  return useMemo(
    () => (game ? overlaySettings[game] : overlaySettings),
    [
      ...games.map((g) => settings[GAME_SHORT_NAMES[g]]?.[OVERLAYS]),
      overlaySettings,
    ],
  );
};

// Enable or disable the overlay thru core
export function setOverlayEnabled(overlay: GameOverlay, enabled: boolean) {
  if (enabled) {
    const gameKey = GAME_SHORT_NAMES[overlay.game];
    const newRecent = [
      ...readState.library.recentlyAddedOverlays,
      `${gameKey}.${overlay.id}`,
    ];
    newRecent[isPersistent] = MAX_TIME;
    writeState.library.recentlyAddedOverlays = newRecent;
  }

  return writeOverlaySetting(overlay.game, overlay.key, enabled);
}

export function removeRecentlyAdded(overlay: GameOverlay) {
  const gameKey = GAME_SHORT_NAMES[overlay.game];

  const overlays = clone(readState.library.recentlyAddedOverlays);
  const indexToRemove = overlays.indexOf(`${gameKey}.${overlay.id}`);
  if (indexToRemove === -1) return;
  overlays.splice(indexToRemove, 1);

  overlays[isPersistent] = MAX_TIME;
  writeState.library.recentlyAddedOverlays = overlays;
}

export function removeRecentlyAddedByGame(game: symbol) {
  const gameKey = GAME_SHORT_NAMES[game];

  const recentlyAddedOverlays = clone(readState.library.recentlyAddedOverlays);
  const overlays = recentlyAddedOverlays.filter(
    (overlay) => !overlay.startsWith(gameKey),
  );
  overlays[isPersistent] = MAX_TIME;
  writeState.library.recentlyAddedOverlays = overlays;
}

export function isOverlayEnabled(overlay: GameOverlay): boolean {
  const enabled = readOverlaySettings(overlay.game, overlay.key);
  if (typeof enabled !== "boolean") {
    devWarn(
      `Expected boolean enabled state for overlay ${
        overlay.id
      } but got ${JSON.stringify(enabled, null, 2)} at key ${overlay.key}`,
    );
    return false;
  }
  return enabled;
}

export function useRecentlyAddedOverlays(game?: symbol) {
  const { library } = useSnapshot(readState);
  const overlays = library.recentlyAddedOverlays;

  return useMemo(() => {
    const hash = overlays.reduce((a, b) => {
      const [key, value] = b.split(".");
      const symbol = GAME_SYMBOL_BY_SHORT_NAME[key];
      if (!game || symbol === game) (a[symbol] ??= []).push(value);
      return a;
    }, {});
    return game ? hash[game] : hash;
  }, [game, overlays]);
}

// Types
type BenchmarkVersusType = "none" | "division" | "pro_player";
type LoLRank = keyof typeof RANK_SYMBOLS;
type GameDbSettings = {
  [GAME_SYMBOL_LOL]: {
    isSummonersRiftOverlayEnabled: boolean;
    isItemDifferenceOverlayEnabled: boolean;
    isSupportOverlayEnabled: boolean;
    isJunglePathingOverlayEnabled2023: boolean;
    isJunglePathingDisplayOptionsSimpleEnabled: boolean;
    junglePathingDisplayOptionManuallySet: false;
    isSummonersRiftSkillOverlayEnabled2023: boolean;
    isSummonersRiftJungleOverlayAlwaysVisible: boolean;
    isSummonersRiftJungleOverlayEnabled: boolean;
    isHealthRelicOverlayEnabled: boolean;
    isArenaAugmentsEnabled: boolean;
    isLoadingScreenOverlayEnabled: boolean;
    isUltimateTimersEnabled: boolean;
    junglePathingBenchmarks: {
      benchmark: BenchmarkVersusType;
      leagueDivision: LoLRank;
      manualSetDivision: boolean;
    };
    isTrinketEnabled: boolean;
    csBenchmarks: {
      benchmark: BenchmarkVersusType;
      leagueDivision: LoLRank;
      manualSetDivision: boolean;
      pros?: Record<keyof typeof ROLE_SYMBOLS, string>;
    };
    wardingLocation: {
      isStandardLocation: boolean;
      distance: number;
    };
    skillOrder: {
      displayOption: string;
    };
    benchmarking: {
      isCsPerMinuteEnable: boolean;
      csPerMinute: BenchmarkVersusType;
      isGoldPerMinuteEnabled: boolean;
      isKillParticipationEnabled: boolean;
      isChampionLevelEnabled: boolean;
      isKdaEnabled: boolean;
      isVisionPerMinuteEnable: boolean;
      isSupportItemTimerEnabled: boolean;
    };
    suppBenchmarks: {
      benchmark: BenchmarkVersusType;
      leagueDivision: LoLRank;
      manualSetDivision: boolean;
    };
    calculatedCS: boolean;
    displayOption: "basic" | "legacy" | "detailed";
    srOverlayToggleKey: {
      value: string;
      code: number;
    };
    isAlwaysVisible: boolean;
    clickThrough: boolean;
  };
};
export type GameOverlayData<T = unknown> = T extends keyof GameDbSettings
  ? GameDbSettings[T]
  : Record<PropertyKey, unknown>;
